import React, {useEffect, useMemo, useState} from "react";
import {Button} from "../button";
import {Popover, PopoverContent, PopoverTrigger} from "../popover";
import {useTranslation} from "react-i18next";
import {cn, createTimeOptions} from "../../lib/fns";
import {ClockCircle} from "../../assets/svgs/ClockCircle";

type Props = {
  value: HourType
  onChange: (value: HourType) => void
  label: string
}
export type HourType = {
  hour: number;
  minute: number;
}

export function TimePicker({
                             value: valueProp, onChange, label,
                           }: Props) {
  const {t} = useTranslation("dashboard");
  const [open, setOpen] = useState(false)
  const [timeValue, setTimeValue] = useState<HourType>(valueProp)
  const hourOptions = createTimeOptions(24);
  const minuteOptions = createTimeOptions(60)
  
  useEffect(() => {
    if (valueProp) {
      setTimeValue(valueProp);
    }
  }, [valueProp])
  
  const handleConfirm = () => {
    if (timeValue) {
      onChange(timeValue);
      setOpen(false);
    }
  }
  const handleTimeChange = (type: "hour" | "minute", value: number) => {
    if (type === "hour") setTimeValue(prev => ({...prev, hour: value}))
    if (type === "minute") setTimeValue(prev => ({...prev, minute: value}))
  }
  
  const displayValue = useMemo(() => {
    return String(timeValue?.hour ?? 0)
    .padStart(2, "0") + ":" + String(timeValue?.minute ?? 0)
    .padStart(2, "0")
  }, [timeValue]);
  
  return (<Popover open={open} onOpenChange={setOpen}>
    <div className="flex flex-col gap-1">
      <div className="text-sm h-[22px] text-neutral-900 font-semibold">
        {label}
      </div>
      <PopoverTrigger asChild>
        <Button>
            <span className={"flex-1 text-left"}>
              {displayValue}
            </span>
          <ClockCircle className="size-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
    </div>
    
    <PopoverContent className="w-auto bg-card rounded-lg shadow-lg p-2" align="start" sideOffset={4}
                    onPointerDown={(e) => e.stopPropagation()}>
      <div className="grid grid-cols-2 h-36 gap-2 mb-3 overflow-scroll">
        {/* Hours Column */}
        <div className="w-[33px] h-full hide-scroll">
          {hourOptions.map(({label, value}) => (<button
            key={value}
            onClick={() => handleTimeChange("hour", value)}
            className={cn("w-full h-6 text-md hover:bg-accent transition-colors flex items-center justify-center", timeValue?.hour === value && "bg-accent hover:bg-primary")}
          >
            {label}
          </button>))}
        </div>
        {/* Minutes Column */}
        <div className="w-[33px] h-full hide-scroll">
          {minuteOptions.map(({value, label}) => (<button
            key={value}
            onClick={() => handleTimeChange("minute", value)}
            className={cn("w-full h-6 text-md hover:bg-accent transition-colors flex items-center justify-center", timeValue?.minute === value && "bg-accent hover:bg-primary")}
          >
            {label}
          </button>))}
        </div>
      </div>
      {/* Confirm button */}
      <Button
        size="sm"
        variant={"primary"}
        className="w-full"
        disabled={!timeValue}
        onClick={handleConfirm}
      >
        {t("ok")}
      </Button>
    </PopoverContent>
  </Popover>)
}
